// Head to Head + Head to Head Trends — types + shaping (Phase 3).
//
// Both widgets read the same source: a list of past meetings for a fixture,
// keyed by the two clubs sorted alphabetically ("Arsenal v Everton"). Pure +
// framework-free.
//
// - Head to Head: the raw match list, most-recent first, optionally filtered to
//   the fixture-home team's home/away meetings, with the winner side flagged.
// - Head to Head Trends: aggregate stats (avg goals, over lines, BTTS, draw %)
//   over the last N seasons, as stat bars (same shape family as Team Discipline).

/** One past meeting. `score` uses an en-dash separator, e.g. "2–1". */
export interface H2HMatch {
  home: string;
  away: string;
  score: string;
  date: string;
}

/** H2H data keyed by the two clubs sorted alphabetically ("A v B"). */
export type H2HData = Record<string, H2HMatch[]>;

/** The alphabetically-sorted lookup key for a "Home v Away" fixture. */
export function h2hKey(fixture: string): string {
  const [t1, t2] = fixture.split(" v ").map((s) => s.trim());
  const [ka, kb] = [t1 ?? "", t2 ?? ""].sort();
  return `${ka} v ${kb}`;
}

// ── Head to Head ────────────────────────────────────────────────────────────

/** A shaped H2H row: the match plus winner flags and a stable join key. */
export interface H2HRow {
  home: string;
  away: string;
  score: string;
  date: string;
  /** Home side won (goals[0] > goals[1]). */
  homeWin: boolean;
  /** Away side won (goals[1] > goals[0]). */
  awayWin: boolean;
  /** Stable per-row key (home + date), matching the legacy d3 join. */
  key: string;
}

/** Venue filter for H2H, relative to the fixture's HOME team. */
export type H2HVenue = "All" | "Home" | "Away" | string;

/**
 * Shape the H2H match list for a fixture, most-recent first. `venue` filters to
 * the fixture-home team's home ("Home") or away ("Away") meetings; "All" (the
 * default) keeps everything. Winner flags come from the score's two goal counts.
 */
export function shapeH2H(
  data: H2HData,
  fixture: string,
  venue: H2HVenue
): H2HRow[] {
  const t1 = (fixture.split(" v ")[0] ?? "").trim();
  const all = data[h2hKey(fixture)] ?? [];
  const desc = [...all].reverse();
  const filter = venue || "All";
  const filtered =
    filter === "Home"
      ? desc.filter((m) => m.home === t1)
      : filter === "Away"
        ? desc.filter((m) => m.away === t1)
        : desc;

  return filtered.map((m) => {
    const g = m.score.split("–").map(Number);
    return {
      home: m.home,
      away: m.away,
      score: m.score,
      date: m.date,
      homeWin: (g[0] ?? 0) > (g[1] ?? 0),
      awayWin: (g[1] ?? 0) > (g[0] ?? 0),
      key: m.home + m.date,
    };
  });
}

// ── Head to Head Trends ─────────────────────────────────────────────────────

/** Seasons control: how much history to include. */
export type H2HSeasons = "3" | "5" | "All" | string;

/** One trend stat bar: value, bar-scale max, formatted text, label + colour. */
export interface H2HTrendRow {
  label: string;
  colour: string;
  val: number;
  max: number;
  text: string;
}

// The six trend rows in display order (labels + colours match the legacy).
const TREND_DEFS: { label: string; colour: string }[] = [
  { label: "Avg Goals", colour: "#3b82f6" },
  { label: "Over 1.5", colour: "#2ecc71" },
  { label: "Over 2.5", colour: "#27ae60" },
  { label: "Over 3.5", colour: "#1d8348" },
  { label: "BTTS", colour: "#8b5cf6" },
  { label: "Draw %", colour: "#f59e0b" },
];

// Seasons → how many recent meetings to include. Matches the legacy slices:
// "All" = everything, "5" = last 10, default ("3") = last 6.
function sliceForSeasons(all: H2HMatch[], seasons: H2HSeasons): H2HMatch[] {
  if (seasons === "All") return all;
  if (seasons === "5") return all.slice(-10);
  return all.slice(-6);
}

/**
 * Shape the H2H Trends stat bars over the last N seasons of meetings. Avg Goals
 * is scaled to a max of 5.0; the four rate rows to 100. Returns null when there
 * are no meetings (empty state).
 */
export function shapeH2HTrends(
  data: H2HData,
  fixture: string,
  seasons: H2HSeasons
): H2HTrendRow[] | null {
  const all = data[h2hKey(fixture)] ?? [];
  const matches = sliceForSeasons(all, seasons);
  if (!matches.length) return null;

  const parsed = matches.map((m) => {
    const g = m.score.split("–").map(Number);
    return { h: g[0] || 0, a: g[1] || 0 };
  });
  const n = parsed.length;
  const pct = (count: number): number => Math.round((count / n) * 100);

  const avgGoals = (parsed.reduce((s, m) => s + m.h + m.a, 0) / n).toFixed(2);
  const over15 = pct(parsed.filter((m) => m.h + m.a > 1).length);
  const over25 = pct(parsed.filter((m) => m.h + m.a > 2).length);
  const over35 = pct(parsed.filter((m) => m.h + m.a > 3).length);
  const btts = pct(parsed.filter((m) => m.h > 0 && m.a > 0).length);
  const drawRate = pct(parsed.filter((m) => m.h === m.a).length);

  const vals: { val: number; max: number; text: string }[] = [
    { val: parseFloat(avgGoals), max: 5.0, text: avgGoals },
    { val: over15, max: 100, text: `${over15}%` },
    { val: over25, max: 100, text: `${over25}%` },
    { val: over35, max: 100, text: `${over35}%` },
    { val: btts, max: 100, text: `${btts}%` },
    { val: drawRate, max: 100, text: `${drawRate}%` },
  ];

  return TREND_DEFS.map((d, i) => ({
    label: d.label,
    colour: d.colour,
    val: vals[i]!.val,
    max: vals[i]!.max,
    text: vals[i]!.text,
  }));
}
